import { AnalyticsEvent } from './events';
import { toItemListViews } from './itemList';
import { YM_HIT_EVENT } from './providers/YandexMetrikaProvider';
import {
  toYandexEcommerce,
  toYandexImpressions,
  toYandexPurchase,
} from './providers/yandexEcommerce';
import type { EventRegistryEntry } from './types';

export const EVENT_REGISTRY: Record<string, EventRegistryEntry> = {
  // === Firebase GA4 events (from analyticsEventsEmitter) ===
  [AnalyticsEvent.VIEW_CART]: {
    providers: ['firebase'],
    eventNames: { firebase: 'view_cart' },
  },
  [AnalyticsEvent.VIEW_ITEM]: {
    providers: ['firebase'],
    eventNames: { firebase: 'view_item' },
  },
  [AnalyticsEvent.PRODUCT_SNIPPET_CLICKED]: {
    providers: ['firebase', 'yandexMetrika'],
    eventNames: { firebase: 'select_item' },
    transformers: { yandexMetrika: toYandexEcommerce('click') },
  },
  [AnalyticsEvent.LOGIN_SCREEN_SHOWN]: {
    providers: ['firebase'],
    eventNames: { firebase: 'login_screen_shown' },
  },
  [AnalyticsEvent.ONBOARDING_COMPLETE]: {
    providers: ['firebase'],
    eventNames: { firebase: 'onboarding_complete' },
  },
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN]: {
    providers: ['firebase'],
    eventNames: { firebase: 'onboarding_subscribe_dialog_shown' },
  },
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'onboarding_subscribe_dialog_clicked' },
  },
  [AnalyticsEvent.CATALOG_SEARCH_VISITED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'catalog_search_visited' },
  },
  [AnalyticsEvent.CATALOG_SEARCH_CLICKED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'catalog_search_clicked' },
  },
  [AnalyticsEvent.CATALOG_CATEGORY_VISITED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'catalog_category_visited' },
  },
  [AnalyticsEvent.CATALOG_CATEGORY_CLICKED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'catalog_category_clicked' },
  },

  // === Common BI+RN events (from analyticsEventsEmitter COMMON_ANALYTICS_*) ===
  [AnalyticsEvent.CATALOG_CATEGORY_CLICKED_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_CATEGORY_VISITED_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_PRODUCT_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_PRODUCT_VISITED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_SEARCH_CLICKED_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_SEARCH_VISITED_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.LOGIN_SCREEN_SHOWN_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.VIEW_CART_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ONBOARDING_COMPLETE_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CART_CHANGED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFERRAL_PROGRAM_OPEN]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFERRAL_PROGRAM_SHARE]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.RATE_PRODUCT_DETAILED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.KYC_VERIFICATION_STATUS]: {
    providers: ['bi', 'reactNative'],
  },

  // === Events from mainStore.sendAnalytics() ===
  [AnalyticsEvent.PURCHASE_COMPLETED]: {
    providers: ['bi', 'reactNative', 'yandexMetrika', 'firebase'],
    eventNames: { firebase: 'purchase__completed' },
  },
  [AnalyticsEvent.PURCHASE_PAYMENT_STARTED]: {
    providers: ['bi', 'reactNative', 'yandexMetrika', 'firebase'],
    eventNames: { firebase: 'purchase__payment_started' },
  },
  [AnalyticsEvent.PURCHASE_CART_DELETED]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'purchase__cart_deleted' },
  },
  [AnalyticsEvent.PURCHASE_START_CHECKOUT]: {
    providers: ['bi', 'reactNative', 'yandexMetrika'],
  },
  [AnalyticsEvent.PURCHASE_PROMOCODE_ENTERED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.PURCHASE_PRODUCT_ADDED_TO_CART]: {
    providers: ['bi', 'reactNative', 'yandexMetrika'],
  },
  [AnalyticsEvent.PURCHASE_PRODUCT_REMOVED_FROM_CART]: {
    providers: ['bi', 'reactNative', 'yandexMetrika'],
  },
  [AnalyticsEvent.DELIVERY_INSTRUCTIONS]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.GENERAL_ETA_CHANGED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.SUBSCRIBE_CHANGE_SETTINGS]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'subscribe__change_settings' },
  },
  [AnalyticsEvent.CATALOG_SEARCHED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CATALOG_SEARCH_IDEA_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REORDER_BUTTON_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ORDER_PAGE_VIEW]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CHECK_BUTTON_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.INVITE_FRIENDS_NOW]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFERRAL_PROGRAM_COPY_CODE]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_BUTTON_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_TYPE_SELECTED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_COMMENT_SUBMITTED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_REQUEST_SUBMITTED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_SUCCESS]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.REFUND_FAIL]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PAGE_VIEWED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CHARGES_EXPANDED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_BUY_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CHECKOUT_VIEWED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CARD_SUBMITTED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_3DS_SHOWN]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PURCHASE_SUCCEEDED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PURCHASE_FAILED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CANCEL_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CANCELLED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_PLAN_CHANGE_KEPT]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATE_VIEWED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_CARD_UPDATE_FAILED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_ENTRY_VIEWED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_ENTRY_CLICKED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.MEMBERSHIP_TIER_HINT_VIEWED]: {
    providers: ['bi', 'reactNative'],
  },

  // === Meta Pixel events ===
  [AnalyticsEvent.PIXEL_PURCHASE]: {
    providers: ['metaPixel'],
    eventNames: { metaPixel: 'Purchase' },
  },
  [AnalyticsEvent.PIXEL_ADD_TO_CART]: {
    providers: ['metaPixel'],
    eventNames: { metaPixel: 'AddToCart' },
  },
  [AnalyticsEvent.PIXEL_INITIATE_CHECKOUT]: {
    providers: ['metaPixel'],
    eventNames: { metaPixel: 'InitiateCheckout' },
  },
  [AnalyticsEvent.PIXEL_VIEW_CONTENT]: {
    providers: ['metaPixel'],
    eventNames: { metaPixel: 'ViewContent' },
  },
  [AnalyticsEvent.PIXEL_PAGE_VIEW]: {
    providers: ['metaPixel'],
    eventNames: { metaPixel: 'PageView' },
  },

  // === Direct Firebase/RN events (from sendToRN) ===
  [AnalyticsEvent.ADD_TO_CART]: {
    providers: ['firebase', 'yandexMetrika'],
    eventNames: { firebase: 'add_to_cart' },
    transformers: { yandexMetrika: toYandexEcommerce('add') },
  },
  [AnalyticsEvent.REMOVE_FROM_CART]: {
    providers: ['bi', 'firebase', 'reactNative', 'yandexMetrika'],
    eventNames: { firebase: 'remove_from_cart' },
    transformers: { yandexMetrika: toYandexEcommerce('remove') },
  },
  [AnalyticsEvent.PRODUCT_ADDED_TO_FAVORITES]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ADD_TO_WISHLIST]: {
    providers: ['firebase'],
    eventNames: { firebase: 'add_to_wishlist' },
  },
  [AnalyticsEvent.SELECT_PROMOTION]: {
    providers: ['firebase'],
    eventNames: { firebase: 'select_promotion' },
  },
  [AnalyticsEvent.AF_FIRST_PURCHASE]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.FIRST_OPEN]: {
    providers: ['gtm'],
    eventNames: { gtm: 'first_open' },
  },
  [AnalyticsEvent.FIRST_PURCHASE]: {
    providers: ['firebase', 'gtm'],
    eventNames: { firebase: 'first_purchase', gtm: 'first_purchase' },
  },
  // GTM only: the payload carries hashed identifiers that must not reach GA4
  [AnalyticsEvent.TIKTOK_FIRST_PURCHASE]: {
    providers: ['gtm'],
    eventNames: { gtm: 'tiktok_first_purchase' },
  },
  [AnalyticsEvent.PURCHASE]: {
    providers: ['firebase', 'yandexMetrika'],
    eventNames: { firebase: 'purchase' },
    transformers: { yandexMetrika: toYandexPurchase },
  },
  [AnalyticsEvent.ADD_PAYMENT_INFO]: {
    providers: ['firebase'],
    eventNames: { firebase: 'add_payment_info' },
  },
  [AnalyticsEvent.BEGIN_CHECKOUT]: {
    providers: ['firebase'],
    eventNames: { firebase: 'begin_checkout' },
  },
  [AnalyticsEvent.CATALOG_HOME_PAGE_VISITED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.DELIVERY_METHOD_SELECT_TIME]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'delivery_method__select_time' },
  },
  [AnalyticsEvent.DELIVERY_METHOD_SELECTED]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'delivery_method__selected' },
  },
  [AnalyticsEvent.PURCHASE_PAYMENT_METHOD_FILLED_IN]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'add_payment_info' },
  },
  [AnalyticsEvent.LOGIN_PHONE_ENTERED]: {
    providers: ['bi', 'reactNative', 'firebase'],
    eventNames: { firebase: 'login_phone_entered' },
  },
  [AnalyticsEvent.GENERAL_ADDRESS_CHANGED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.ONBOARDING_WAREHOUSE_SELECTED]: {
    providers: ['bi', 'reactNative', 'firebase', 'yandexMetrika'],
    eventNames: { firebase: 'onboarding_warehouse_selected' },
  },
  [AnalyticsEvent.EVENT_APP_FEEDBACK]: {
    providers: ['reactNative'],
  },
  [AnalyticsEvent.SEARCH]: {
    providers: ['firebase'],
    eventNames: { firebase: 'search' },
  },
  [AnalyticsEvent.LOGIN_CODE_ENTERED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.LOGIN_CODE_ENTERED_FIREBASE]: {
    providers: ['firebase'],
    eventNames: { firebase: 'login_code_entered' },
  },
  [AnalyticsEvent.LOGIN]: {
    providers: ['bi', 'firebase', 'reactNative'],
    eventNames: { firebase: 'login' },
  },
  [AnalyticsEvent.RATE_ORDER_GENERAL]: {
    providers: ['reactNative'],
  },
  [AnalyticsEvent.RATE_ORDER_GENERAL_FIREBASE]: {
    providers: ['firebase'],
    eventNames: { firebase: 'rate_order_general' },
  },
  [AnalyticsEvent.RATE_ORDER_DETAILED]: {
    providers: ['reactNative'],
  },
  [AnalyticsEvent.RATE_ORDER_DETAILED_FIREBASE]: {
    providers: ['firebase'],
    eventNames: { firebase: 'rate_order_detailed' },
  },
  [AnalyticsEvent.CATALOG_CATEGORY_SCROLLED]: {
    providers: ['reactNative'],
  },
  [AnalyticsEvent.CATALOG_CATEGORY_SCROLLED_FIREBASE]: {
    providers: ['firebase'],
    eventNames: { firebase: 'catalog_category_scrolled' },
  },
  [AnalyticsEvent.PROFILE_DELETED]: {
    providers: ['firebase'],
    eventNames: { firebase: 'profile_deleted' },
  },

  // === Yandex Metrika-specific events ===
  [AnalyticsEvent.CLICK_BUY]: {
    providers: ['yandexMetrika'],
  },
  [AnalyticsEvent.VIEW_OFFER]: {
    providers: ['yandexMetrika'],
    transformers: { yandexMetrika: toYandexEcommerce('detail') },
  },

  // === Phase 6: New analytics events ===
  [AnalyticsEvent.PAGE_VIEW_BI]: {
    providers: ['bi'],
  },
  // Ungated on purpose: the tag's own init hit, clickmap and webvisor already
  // run before the banner is answered, while gating page hits would make every
  // page funnel drop by the banner refusal rate.
  [AnalyticsEvent.PAGE_VIEW_YM]: {
    providers: ['yandexMetrika'],
    requiresConsent: false,
    eventNames: { yandexMetrika: YM_HIT_EVENT },
  },
  [AnalyticsEvent.PAYMENT_METHOD_SELECTED]: {
    providers: ['bi', 'firebase', 'reactNative'],
    eventNames: { firebase: 'payment_method_selected' },
  },
  [AnalyticsEvent.ORDER_STATUS_CHANGED]: {
    providers: ['bi', 'reactNative'],
  },
  [AnalyticsEvent.SIGN_UP]: {
    providers: ['bi', 'firebase', 'reactNative'],
    eventNames: { firebase: 'sign_up' },
  },

  // === Batch events ===
  [AnalyticsEvent.CATALOG_PRODUCT_SNIPPET_SHOWN]: {
    providers: ['bi', 'firebase', 'yandexMetrika'],
    eventNames: { firebase: 'view_item_list' },
    batchable: true,
    batchTransformers: {
      firebase: toItemListViews,
      yandexMetrika: toYandexImpressions,
    },
  },
};
